/**
 * Safe Storage Utility
 *
 * Provides a safe wrapper around localStorage and sessionStorage to prevent
 * null reference errors in Android WebView and other environments where
 * storage APIs may be unavailable or disabled.
 *
 * Features:
 * - Graceful fallback when storage is unavailable
 * - Comprehensive error handling with try-catch blocks
 * - Development-mode logging for debugging
 * - Silent failures in production (no thrown errors)
 * - TypeScript type safety
 * - Storage availability checking
 *
 * @module safe-storage
 */

/**
 * Storage interface that matches the Web Storage API
 */
export interface SafeStorageInterface {
  /**
   * Retrieves an item from storage
   * @param key - The storage key
   * @returns The stored value or null if not found/unavailable
   */
  getItem(key: string): string | null;

  /**
   * Stores an item in storage
   * @param key - The storage key
   * @param value - The value to store
   * @returns true if successful, false otherwise
   */
  setItem(key: string, value: string): boolean;

  /**
   * Removes an item from storage
   * @param key - The storage key
   * @returns true if successful, false otherwise
   */
  removeItem(key: string): boolean;

  /**
   * Clears all items from storage
   * @returns true if successful, false otherwise
   */
  clear(): boolean;

  /**
   * Checks if storage is available and functional
   * @returns true if storage is available, false otherwise
   */
  isAvailable(): boolean;

  /**
   * Gets the number of items in storage
   * @returns The count of stored items, or 0 if unavailable
   */
  length(): number;

  /**
   * Gets the key at the specified index
   * @param index - The index of the key
   * @returns The key at the index, or null if unavailable
   */
  key(index: number): string | null;
}

/**
 * Storage types for logging
 */
type StorageType = 'localStorage' | 'sessionStorage';

/**
 * Creates a safe storage wrapper for a given storage object
 * @param storage - The native storage object (localStorage or sessionStorage)
 * @param storageType - The type of storage for logging purposes
 * @returns A safe storage interface
 */
function createSafeStorage(
  storage: Storage | null | undefined,
  storageType: StorageType
): SafeStorageInterface {
  /**
   * Logs errors in development mode only
   */
  const logError = (operation: string, error: unknown): void => {
    if (import.meta.env.DEV) {
      console.warn(
        `[${storageType}] ${operation} failed:`,
        error instanceof Error ? error.message : String(error)
      );
    }
  };

  /**
   * Logs warnings in development mode only
   */
  const logWarning = (message: string): void => {
    if (import.meta.env.DEV) {
      console.warn(`[${storageType}] ${message}`);
    }
  };

  /**
   * Checks if the storage object is available
   */
  const checkAvailability = (): boolean => {
    if (!storage) {
      return false;
    }

    try {
      // Test if we can actually use the storage
      const testKey = '__storage_test__';
      storage.setItem(testKey, 'test');
      storage.removeItem(testKey);
      return true;
    } catch (error) {
      logWarning(
        `Storage is present but not functional: ${error instanceof Error ? error.message : String(error)}`
      );
      return false;
    }
  };

  // Cache availability check result
  let isStorageAvailable: boolean | null = null;

  const isAvailable = (): boolean => {
    if (isStorageAvailable === null) {
      isStorageAvailable = checkAvailability();
    }
    return isStorageAvailable;
  };

  const getItem = (key: string): string | null => {
    if (!isAvailable()) {
      return null;
    }

    try {
      return storage?.getItem(key) ?? null;
    } catch (error) {
      logError(`getItem("${key}")`, error);
      return null;
    }
  };

  const setItem = (key: string, value: string): boolean => {
    if (!isAvailable()) {
      return false;
    }

    try {
      storage?.setItem(key, value);
      return true;
    } catch (error) {
      // Handle QuotaExceededError specifically
      if (
        error instanceof DOMException &&
        error.name === 'QuotaExceededError'
      ) {
        logError(
          `setItem("${key}")`,
          'Storage quota exceeded. Consider clearing old data.'
        );
      } else {
        logError(`setItem("${key}")`, error);
      }
      return false;
    }
  };

  const removeItem = (key: string): boolean => {
    if (!isAvailable()) {
      return false;
    }

    try {
      storage?.removeItem(key);
      return true;
    } catch (error) {
      logError(`removeItem("${key}")`, error);
      return false;
    }
  };

  const clear = (): boolean => {
    if (!isAvailable()) {
      return false;
    }

    try {
      storage?.clear();
      return true;
    } catch (error) {
      logError('clear()', error);
      return false;
    }
  };

  const length = (): number => {
    if (!isAvailable()) {
      return 0;
    }

    try {
      return storage?.length ?? 0;
    } catch (error) {
      logError('length', error);
      return 0;
    }
  };

  const key = (index: number): string | null => {
    if (!isAvailable()) {
      return null;
    }

    try {
      return storage?.key(index) ?? null;
    } catch (error) {
      logError(`key(${index})`, error);
      return null;
    }
  };

  return {
    getItem,
    setItem,
    removeItem,
    clear,
    isAvailable,
    length,
    key,
  };
}

/**
 * Safe wrapper for localStorage
 *
 * Provides a drop-in replacement for localStorage that handles null references
 * and other errors gracefully. Safe to use in all environments including
 * Android WebView.
 *
 * @example
 * ```typescript
 * // Check if storage is available
 * if (safeStorage.isAvailable()) {
 *   console.log('localStorage is available');
 * }
 *
 * // Store a value (returns boolean success status)
 * const success = safeStorage.setItem('user_id', '12345');
 * if (!success) {
 *   console.warn('Failed to store user_id');
 * }
 *
 * // Retrieve a value (returns null if unavailable)
 * const userId = safeStorage.getItem('user_id');
 * if (userId) {
 *   console.log('User ID:', userId);
 * }
 *
 * // Remove a value
 * safeStorage.removeItem('user_id');
 *
 * // Clear all storage
 * safeStorage.clear();
 * ```
 */
export const safeStorage = createSafeStorage(
  typeof window !== 'undefined' ? window.localStorage : null,
  'localStorage'
);

/**
 * Safe wrapper for sessionStorage
 *
 * Provides a drop-in replacement for sessionStorage that handles null references
 * and other errors gracefully. Data stored here persists only for the page session.
 *
 * @example
 * ```typescript
 * // Store temporary session data
 * safeSessionStorage.setItem('temp_token', 'abc123');
 *
 * // Retrieve session data
 * const token = safeSessionStorage.getItem('temp_token');
 * ```
 */
export const safeSessionStorage = createSafeStorage(
  typeof window !== 'undefined' ? window.sessionStorage : null,
  'sessionStorage'
);

/**
 * Utility to safely parse JSON from storage
 *
 * @param key - The storage key
 * @param storage - The storage interface to use (defaults to safeStorage)
 * @returns The parsed object or null if parsing fails
 *
 * @example
 * ```typescript
 * interface UserPrefs {
 *   theme: 'light' | 'dark';
 *   language: string;
 * }
 *
 * const prefs = safeGetJSON<UserPrefs>('user_prefs');
 * if (prefs) {
 *   console.log('Theme:', prefs.theme);
 * }
 * ```
 */
export function safeGetJSON<T = unknown>(
  key: string,
  storage: SafeStorageInterface = safeStorage
): T | null {
  const value = storage.getItem(key);
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`[safeGetJSON] Failed to parse "${key}":`, error);
    }
    return null;
  }
}

/**
 * Utility to safely stringify and store JSON in storage
 *
 * @param key - The storage key
 * @param value - The value to store
 * @param storage - The storage interface to use (defaults to safeStorage)
 * @returns true if successful, false otherwise
 *
 * @example
 * ```typescript
 * const prefs = { theme: 'dark', language: 'en' };
 * const success = safeSetJSON('user_prefs', prefs);
 * ```
 */
export function safeSetJSON(
  key: string,
  value: unknown,
  storage: SafeStorageInterface = safeStorage
): boolean {
  try {
    const jsonString = JSON.stringify(value);
    return storage.setItem(key, jsonString);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`[safeSetJSON] Failed to stringify/store "${key}":`, error);
    }
    return false;
  }
}

/**
 * Utility to get all keys from storage
 *
 * @param storage - The storage interface to use (defaults to safeStorage)
 * @returns Array of all keys in storage
 *
 * @example
 * ```typescript
 * const keys = safeGetAllKeys();
 * console.log('Stored keys:', keys);
 * ```
 */
export function safeGetAllKeys(
  storage: SafeStorageInterface = safeStorage
): string[] {
  if (!storage.isAvailable()) {
    return [];
  }

  const keys: string[] = [];
  const len = storage.length();

  for (let i = 0; i < len; i++) {
    const key = storage.key(i);
    if (key !== null) {
      keys.push(key);
    }
  }

  return keys;
}

/**
 * Utility to remove multiple keys from storage
 *
 * @param keys - Array of keys to remove
 * @param storage - The storage interface to use (defaults to safeStorage)
 * @returns Number of successfully removed keys
 *
 * @example
 * ```typescript
 * const removed = safeRemoveMultiple(['key1', 'key2', 'key3']);
 * console.log(`Removed ${removed} keys`);
 * ```
 */
export function safeRemoveMultiple(
  keys: string[],
  storage: SafeStorageInterface = safeStorage
): number {
  let successCount = 0;

  for (const key of keys) {
    if (storage.removeItem(key)) {
      successCount++;
    }
  }

  return successCount;
}

/**
 * Utility to check if a key exists in storage
 *
 * @param key - The storage key to check
 * @param storage - The storage interface to use (defaults to safeStorage)
 * @returns true if the key exists, false otherwise
 *
 * @example
 * ```typescript
 * if (safeHasKey('user_token')) {
 *   console.log('User is logged in');
 * }
 * ```
 */
export function safeHasKey(
  key: string,
  storage: SafeStorageInterface = safeStorage
): boolean {
  return storage.getItem(key) !== null;
}

/**
 * Type guard to check if storage is available
 * Useful for conditional logic that depends on storage
 *
 * @param storage - The storage interface to check (defaults to safeStorage)
 * @returns true if storage is available and functional
 *
 * @example
 * ```typescript
 * if (isStorageAvailable()) {
 *   // Proceed with storage-dependent features
 * } else {
 *   // Use alternative approach (e.g., in-memory cache)
 * }
 * ```
 */
export function isStorageAvailable(
  storage: SafeStorageInterface = safeStorage
): boolean {
  return storage.isAvailable();
}

// Log storage availability on module load (development only)
if (import.meta.env.DEV) {
  console.log('[safe-storage] Module loaded');
  console.log('  - localStorage available:', safeStorage.isAvailable());
  console.log(
    '  - sessionStorage available:',
    safeSessionStorage.isAvailable()
  );
}
